import React, { useState, useEffect } from 'react'
import { CheckCircle, FileText } from 'lucide-react'
import { 
  TimesheetBuilder, 
  TimesheetStatusBar, 
  TimesheetHistory 
} from '../components/timesheet'
import { useThemeTokens } from '../themes'
import { useTimesheetHistoryState } from '../lib/timesheet-utils'
import type { DailyTimesheet } from '../contracts/timesheet'

export const Timesheet: React.FC = () => {
  const theme = useThemeTokens()
  const [submitSuccess, setSubmitSuccess] = useState<DailyTimesheet | null>(null)
  
  // Use enhanced state management with history
  const {
    timesheet,
    historyData,
    historyCollapsed,
    setHistoryCollapsed,
    switchToDate,
    copyWorkersFromDate,
    loadTodaysDraft,
    loadHistoryData
  } = useTimesheetHistoryState()

  // 🔥 FIX: Load history data on component mount
  useEffect(() => {
    loadHistoryData()
  }, [loadHistoryData])

  const handleSubmitSuccess = (submittedTimesheet: DailyTimesheet) => {
    setSubmitSuccess(submittedTimesheet)
    
    // Refresh history to show updated status
    loadHistoryData()
    
    // TDD: REMOVED auto-navigation - user should stay on same page
    // setTimeout(() => {
    //   navigate('/timesheet-reports')
    // }, 3000)
  }

  // Page container styles with sidebar layout
  const pageStyles: React.CSSProperties = {
    minHeight: '100vh',
    backgroundColor: theme.colors.background,
    display: 'flex'
  }

  // Main content styles
  const mainContentStyles: React.CSSProperties = {
    flex: 1,
    padding: theme.spacing.lg,
    paddingLeft: theme.spacing.md, // Less left padding since sidebar handles spacing
    minWidth: 0 // Prevent flex item from overflowing
  }

  // Header styles
  const headerStyles: React.CSSProperties = {
    marginBottom: theme.spacing.md,
    textAlign: 'center'
  }

  // Success message styles
  const successStyles: React.CSSProperties = {
    backgroundColor: theme.colors.success + '15',
    border: `2px solid ${theme.colors.success}`,
    borderRadius: theme.borderRadius.md,
    padding: theme.spacing.lg,
    marginBottom: theme.spacing.xl,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing.md,
    color: theme.colors.success,
    fontSize: theme.typography.fontSize.lg,
    fontWeight: theme.typography.fontWeight.medium
  }

  return (
    <div style={pageStyles}>
      {/* History Sidebar */}
      <TimesheetHistory 
        historyData={historyData}
        currentDate={timesheet?.date || ''}
        isCollapsed={historyCollapsed}
        onToggle={() => setHistoryCollapsed(!historyCollapsed)}
        onDateSelect={switchToDate}
        onViewTimesheet={switchToDate}
        onCopyWorkers={copyWorkersFromDate}
        data-testid="timesheet-history"
      />
      
      {/* Main Content */}
      <div style={mainContentStyles}>
        {/* Page Header */}
        <div style={headerStyles}>
          <h1 style={{
            fontSize: theme.typography.fontSize.lg,
            fontWeight: theme.typography.fontWeight.bold,
            color: theme.colors.text,
            margin: 0,
            marginBottom: theme.spacing.xs,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: theme.spacing.sm
          }}>
            <FileText size={24} color={theme.colors.primary} />
            Daily Timesheet
          </h1>
          <p style={{
            fontSize: theme.typography.fontSize.sm,
            color: theme.colors.textSecondary,
            margin: 0,
            maxWidth: '500px',
            marginLeft: 'auto',
            marginRight: 'auto'
          }}>
            Drag workers to timesheet, adjust times, and submit when complete.
          </p>
        </div>

        {/* Status Bar */}
        {timesheet && (
          <TimesheetStatusBar 
            currentDate={timesheet.date}
            status={timesheet.status}
            lastSavedAt={timesheet.lastSavedAt}
            draftWorkerCount={timesheet.totalWorkers}
            totalHours={timesheet.totalHours}
            onLoadDraft={loadTodaysDraft}
            onStartFresh={() => {/* Will implement */}}
            onCopyYesterday={() => {/* Will implement */}}
          />
        )}

        {/* Success Message */}
        {submitSuccess && (
          <div style={successStyles} data-testid="submission-success">
            <CheckCircle size={24} />
            <div>
              <div>Timesheet submitted successfully!</div>
              <div style={{
                fontSize: theme.typography.fontSize.sm,
                fontWeight: theme.typography.fontWeight.normal,
                marginTop: theme.spacing.sm
              }}>
                {submitSuccess.totalWorkers} workers, {submitSuccess.totalHours.toFixed(1)} hours total
              </div>
            </div>
          </div>
        )}

        {/* Timesheet Builder */}
        <TimesheetBuilder
          onSubmitSuccess={handleSubmitSuccess}
        />
      </div>
    </div>
  )
} 